import { api } from './api';
import { Article, ArticleWithAuthor, ShowWithHost, EpisodeWithShow, ContentStatus, MediaType, EventType, FeedItem } from '../types';

// Re-export types for backward compatibility if needed
export type { Article, ArticleWithAuthor, ShowWithHost, EpisodeWithShow };

// Articles
export const getArticles = async (category?: string, featured?: boolean, limit?: number, status?: string): Promise<ArticleWithAuthor[]> => {
  try {
    const params: any = {};
    if (category) params.category = category;
    if (featured) params.featured = 'true';
    if (limit) params.limit = limit;
    if (status) params.status = status;

    return await api.get<ArticleWithAuthor[]>('/articles', params);
  } catch (error) {
    console.error('Error fetching articles:', error);
    return [];
  }
};

export const getAllArticles = async (): Promise<ArticleWithAuthor[]> => {
  return getArticles(undefined, undefined, undefined, 'all');
};

export const getArticlesByStatus = async (status: ContentStatus): Promise<ArticleWithAuthor[]> => {
  // TODO: Add status filter to API
  return getArticles();
};

export const getArticle = async (slug: string): Promise<ArticleWithAuthor | null> => {
  try {
    return await api.get<ArticleWithAuthor>(`/articles/${slug}`);
  } catch (error) {
    return null;
  }
};

export const getArticleBySlug = getArticle; // Alias

export const getArticlesByCategory = async (category: string): Promise<ArticleWithAuthor[]> => {
  return getArticles(category);
};

export const getRelatedArticles = async (articleId: string, tags: string[] = []): Promise<ArticleWithAuthor[]> => {
  try {
    const params: any = {};
    if (tags.length > 0) params.tags = tags.join(',');
    return await api.get<ArticleWithAuthor[]>(`/articles/${articleId}/related`, params);
  } catch (error) {
    return [];
  }
};

// Shows
export const getShows = async (type?: 'audio' | 'video'): Promise<ShowWithHost[]> => {
  try {
    const params: any = {};
    if (type) params.type = type;
    return await api.get<ShowWithHost[]>('/shows', params);
  } catch (error) {
    return [];
  }
};

export const getShow = async (id: string): Promise<ShowWithHost | null> => {
  try {
    return await api.get<ShowWithHost>(`/shows/${id}`);
  } catch (error) {
    return null;
  }
};

// Episodes
export const getEpisodes = async (showId: string): Promise<EpisodeWithShow[]> => {
  try {
    return await api.get<EpisodeWithShow[]>(`/shows/${showId}/episodes`);
  } catch (error) {
    return [];
  }
};

export const getAllEpisodes = async (mediaType?: MediaType): Promise<EpisodeWithShow[]> => {
  try {
    const params: any = {};
    if (mediaType) params.mediaType = mediaType;
    return await api.get<EpisodeWithShow[]>('/episodes', params);
  } catch (error) {
    return [];
  }
};

export const getShowEpisodes = getEpisodes; // Alias

export const getLatestEpisodes = async (limit = 5): Promise<EpisodeWithShow[]> => {
  try {
    return await api.get<EpisodeWithShow[]>('/episodes/latest', { limit });
  } catch (error) {
    return [];
  }
};

export const getEpisode = async (id: string): Promise<EpisodeWithShow | null> => {
  try {
    return await api.get<EpisodeWithShow>(`/episodes/${id}`);
  } catch (error) {
    return null;
  }
};

export const getVideoEpisodes = async (): Promise<EpisodeWithShow[]> => {
  return getAllEpisodes(MediaType.Video);
};

// Feed
export const getFeed = async (): Promise<FeedItem[]> => {
  try {
    const [articles, episodes] = await Promise.all([
      getArticles(undefined, false, 20),
      getAllEpisodes()
    ]);

    const feedArticles = articles.map(a => ({
      ...a,
      type: 'article',
      author: a.author ? {
        id: a.author.id,
        name: a.author.name,
        role: a.author.role,
        avatar: a.author.avatar || ''
      } : null
    }));

    const feedEpisodes = episodes.map(e => ({
      ...e,
      type: 'episode'
    }));

    return [...feedArticles, ...feedEpisodes].sort((a, b) => {
      const dateA = new Date(a.publishedAt || 0).getTime();
      const dateB = new Date(b.publishedAt || 0).getTime();
      return dateB - dateA;
    }) as FeedItem[];
  } catch (error) {
    return [];
  }
};

// Search
export const searchContent = async (query: string): Promise<any[]> => {
  try {
    return await api.get<any[]>('/search', { q: query, limit: 12 });
  } catch (error) {
    return [];
  }
};

// Pages
export const getPage = async (slug: string): Promise<any | null> => {
  try {
    return await api.get<any>(`/pages/${slug}`);
  } catch (error) {
    return null;
  }
};

// CRUD (Admin)
export const createArticle = async (article: Partial<ArticleWithAuthor>, userId: string): Promise<{ article?: ArticleWithAuthor; error?: string }> => {
  try {
    const res = await api.post<{ article: ArticleWithAuthor }>('/articles', { ...article, authorId: userId });
    return { article: res.article };
  } catch (e: any) {
    return { error: e.message };
  }
};

export const updateArticle = async (id: string, article: Partial<ArticleWithAuthor>, userId: string): Promise<{ article?: ArticleWithAuthor; error?: string }> => {
  try {
    const res = await api.patch<{ article: ArticleWithAuthor }>(`/articles/${id}`, { ...article, authorId: userId });
    return { article: res.article };
  } catch (e: any) {
    return { error: e.message };
  }
};

export const updateArticleStatus = async (id: string, status: ContentStatus, userId: string): Promise<{ error?: string }> => {
  try {
    await api.patch(`/articles/${id}`, { status, authorId: userId });
    return {};
  } catch (e: any) {
    return { error: e.message };
  }
};

export const deleteArticle = async (id: string): Promise<{ error?: string }> => {
  try {
    await api.delete(`/articles/${id}`);
    return {};
  } catch (e: any) {
    return { error: e.message };
  }
};

export const getContentStats = async (): Promise<{ publishedArticles: number; draftArticles: number; reviewArticles: number }> => {
  try {
    return await api.get<{ publishedArticles: number; draftArticles: number; reviewArticles: number }>('/stats');
  } catch (error) {
    return { publishedArticles: 0, draftArticles: 0, reviewArticles: 0 };
  }
};


// Shows & Episodes CRUD
export const createShow = async (show: Partial<ShowWithHost>): Promise<{ show?: ShowWithHost; error?: string }> => {
  try {
    const res = await api.post<{ show: ShowWithHost }>('/shows', show);
    return { show: res.show };
  } catch (e: any) {
    return { error: e.message };
  }
};

export const updateShow = async (id: string, show: Partial<ShowWithHost>): Promise<{ show?: ShowWithHost; error?: string }> => {
  try {
    const res = await api.patch<{ show: ShowWithHost }>(`/shows/${id}`, show);
    return { show: res.show };
  } catch (e: any) {
    return { error: e.message };
  }
};

export const createEpisode = async (episode: Partial<EpisodeWithShow>): Promise<{ episode?: EpisodeWithShow; error?: string }> => {
  try {
    const res = await api.post<{ episode: EpisodeWithShow }>('/episodes', episode);
    return { episode: res.episode };
  } catch (e: any) {
    return { error: e.message };
  }
};

// Analytics
export const trackEvent = async (eventType: EventType, contentId?: string, metadata?: any): Promise<void> => {
  try {
    await api.post('/analytics/events', {
      event_type: eventType,
      content_id: contentId,
      path: window.location.pathname,
      metadata
    });
  } catch (error) {
    // fail silently
  }
};

// Newsletter
export const subscribeToNewsletter = async (email: string): Promise<{ success: boolean; error?: string }> => {
  try {
    await api.post('/newsletter/subscribe', { email });
    return { success: true };
  } catch (error: any) {
    return { success: false, error: error.message };
  }
};

export const deleteShow = async (id: string): Promise<{ error?: string }> => {
  try {
    await api.delete(`/shows/${id}`);
    return {};
  } catch (e: any) {
    return { error: e.message };
  }
};